<template>
  <div class="highlight-wrapper" :style="`text-align: ${position};`">
    <span v-if="timestampLoadError">{{ text }}</span>
    <span v-else v-for="(word, index) in words" :key="index">
      <span
        :ref="(el) => (wordRefs[index] = el as HTMLSpanElement)"
        :class="{
          highlighted: index === currentWordIndex,
          hidden: hideUntilHighlight && index > highestHighlightedIndex
        }"
        class="word"
      >
        {{ word.word }}
      </span>
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, inject, computed } from 'vue'
import type { Ref } from 'vue'
import type { AvatarOptions, QuestionTimestamp } from '@/customTypes'
import { storeToRefs } from 'pinia'
import { useDataStore } from '@/stores/DataStore'

const props = defineProps({
  textAudioUrl: String,
  text: String,
  position: { type: String, default: 'center' },
  hideUntilHighlight: { type: Boolean, default: false },
  loadedTimestamps: { type: Array as () => QuestionTimestamp[] | null, default: null }
})

const emit = defineEmits<{
  (e: 'highlighting-finished'): boolean
}>()

const avatarOptions = inject('avatarOptions') as Ref<AvatarOptions>
const activeHighlighter = computed(() => avatarOptions.value?.audio === props.textAudioUrl || false)

const dataStore = useDataStore()
const { currentQuestionTime } = storeToRefs(dataStore)

const wordRefs = ref<{ [key: number]: HTMLSpanElement }>({})
const currentWordIndex = ref<number>(-1)
const highestHighlightedIndex = ref<number>(-1)
const timestamps = ref<QuestionTimestamp[] | null>([])
const timestampLoadError = ref(false)

onMounted(async () => {
  if (props.textAudioUrl && !props.loadedTimestamps?.length) {
    try {
      timestamps.value = (await dataStore.fetchHighlightingTimestamps(props.textAudioUrl)) || null
    } catch (error) {
      timestampLoadError.value = true
    }
  } else {
    timestamps.value = props.loadedTimestamps
  }
})

watch(currentQuestionTime, (newTime) => {
  if (!activeHighlighter.value || !timestamps.value) return

  // Find the word corresponding to the current time
  for (let i = 0; i < timestamps.value.length; i++) {
    const { start, end } = timestamps.value[i]
    if (newTime >= start && newTime <= end) {
      currentWordIndex.value = i
      highestHighlightedIndex.value = Math.max(highestHighlightedIndex.value, i)
      return
    }
  }

  // If no word is found (audio is ahead), reset currentWordIndex
  currentWordIndex.value = -1
})

const words = computed(() => timestamps.value || [])
</script>

<style lang="scss" scoped>
.highlight-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: v-bind(position);
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
  width: 30ch;
  max-width: 90%;
}

.word {
  display: inline-block;
  padding: 0.25rem;

  &.highlighted {
    color: $primary;
  }

  &.hidden {
    opacity: 0;
  }

  &:last-of-type {
    padding-right: 0;
  }
}

@media (max-width: 768px) {
  .highlight-wrapper {
    font-size: 0.9em;
  }
}

@media (max-width: 480px) {
  .highlight-wrapper {
    font-size: 0.8em;
  }
}
</style>
